import { TinyCommunity } from 'types/company/community';
import { useCompanyOrCommunityDashboardData } from 'pages/Dashboard/useDashboardData';
import React, { useState } from 'react';
import { communityUrls } from 'urls';
import CalendarIcon from 'ui/elements/icons/CalendarIcon';
import CompaniesIcon from 'ui/elements/icons/CompanyIcon';
import UserIcon from 'ui/elements/icons/UserIcon';
import InviteUserIcon from 'ui/elements/icons/InviteUserIcon';
import TurnRightIcon from 'ui/elements/icons/TurnRightIcon';
import InviteDialog from 'pages/Community/CommunityManagement/invite/InviteDialog';
import OnYourCompanyAgenda from 'pages/Dashboard/manage/OnYourCompanyAgenda';
import styled from '@emotion/styled';
import { contentSpacing, halfSpacing, sectionSpacing } from 'ui/theme/themeConstants';
import { bluePlanetTheme } from 'ui/theme';
import { useCommunitySummary } from 'apis/CompanyAPI/communities/useCommunitySummary';
import Resources from 'util/resource/Resources';
import { Link } from 'react-router-dom';
import { shape } from 'ui/theme/themeBluePlanet';
import { MobileMargin } from 'ui/views/layouts/BaseLayout';
import PenIcon from 'ui/elements/icons/PenIcon';

export const ActionLinks = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  justify-content: start;
  align-items: stretch;
  gap: ${halfSpacing};

  ${bluePlanetTheme.breakpoints.down('md')} {
    display: flex;
    flex-direction: column;
  }
`;

export const ActionItem = styled(Link)(
  ({ color }: { color?: 'blue' }) => `
  display: flex;
  text-align: center;
  flex-direction: column;
  align-items: center;
  gap: ${halfSpacing};
  background-color: ${color === 'blue' ? bluePlanetTheme.bluePlanetPalette.blue.main : bluePlanetTheme.bluePlanetPalette.white};
  color: ${color === 'blue' ? bluePlanetTheme.bluePlanetPalette.white : bluePlanetTheme.bluePlanetPalette.indigo.main};
  border-radius: ${shape.borderRadius}px;
  padding: ${sectionSpacing} ${contentSpacing};
  &:hover {
    background-color: ${color === 'blue' ? bluePlanetTheme.bluePlanetPalette.blue.dark : bluePlanetTheme.bluePlanetPalette.grey.medium};
    color: ${color === 'blue' ? bluePlanetTheme.bluePlanetPalette.grey.veryLight : bluePlanetTheme.bluePlanetPalette.indigo.dark};
    transition: background-color 0.2s ease-in-out;
  }
  
  ${bluePlanetTheme.breakpoints.down('md')} {
    flex-direction: row;
    padding: ${halfSpacing} ${contentSpacing};
  }
}`,
);

export default function ManageCommunity({ community }: { community: TinyCommunity }) {
  const { resource: dashboardData } = useCompanyOrCommunityDashboardData(community);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const { resource: summary } = useCommunitySummary(community.slug);

  return (
    <Resources resources={[dashboardData, summary]}>
      {([dashboardData, summary]) => (
        <>
          <MobileMargin className="u-content-spacing-y">
            <ActionLinks>
              <ActionItem
                color="blue"
                to={communityUrls.overview(community.slug, 'updates', {
                  isPostingUpdate: true,
                })}
              >
                <PenIcon className="u-half-spacing-right" />
                New post
              </ActionItem>
              <ActionItem to={communityUrls.events.create(community.slug)}>
                <CalendarIcon className="u-half-spacing-right" fontSize="small" />
                Create event
              </ActionItem>
              {summary.configuration.companyMembersIsEnabled && (
                <ActionItem to={communityUrls.manageMembers.companies(community.slug)}>
                  <CompaniesIcon className="u-half-spacing-right" fontSize="small" />
                  Manage companies
                </ActionItem>
              )}
              <ActionItem to={communityUrls.manageMembers.users(community.slug)}>
                <UserIcon className="u-half-spacing-right" fontSize="small" />
                Manage users
              </ActionItem>
              <ActionItem to="#" onClick={() => setIsInviteDialogOpen(true)}>
                <InviteUserIcon className="u-half-spacing-right" fontSize="small" />
                Invite members
              </ActionItem>
              <ActionItem to={communityUrls.overview(community.slug)}>
                <TurnRightIcon className="u-half-spacing-right" fontSize="small" />
                Go to community
              </ActionItem>
            </ActionLinks>
            {isInviteDialogOpen && (
              <InviteDialog communitySlug={community.slug} onClose={() => setIsInviteDialogOpen(false)} />
            )}
          </MobileMargin>
          <div>
            <OnYourCompanyAgenda
              messages={dashboardData.messages.filter(
                it =>
                  ![
                    'welcome-to-company-profile-as-new-company-admin',
                    'should-update-investors-as-company-admin',
                    'should-update-company-profile-as-company-admin',
                  ].includes(it.type),
              )} // We show these in to-do's instead
            />
          </div>
        </>
      )}
    </Resources>
  );
}
